import {
  AnchorIcon,
  BrickWallIcon,
  Building2Icon,
  BuildingIcon,
  CableIcon,
  ConstructionIcon,
  ContainerIcon,
  DamIcon,
  DrillIcon,
  DropletsIcon,
  FactoryIcon,
  FenceIcon,
  FuelIcon,
  HammerIcon,
  HardHatIcon,
  HospitalIcon,
  HouseIcon,
  LandmarkIcon,
  MountainIcon,
  PickaxeIcon,
  PlaneIcon,
  RadioTowerIcon,
  RouteIcon,
  SchoolIcon,
  ShipIcon,
  StoreIcon,
  TentIcon,
  TowerControlIcon,
  TractorIcon,
  TrainTrackIcon,
  TreesIcon,
  TruckIcon,
  WarehouseIcon,
  WavesIcon,
  WrenchIcon,
  ZapIcon,
  type LucideIcon,
} from 'lucide-react';

/**
 * A job's look: an accent colour, an icon and an optional cover photo, chosen in the job's settings
 * (or by a platform admin). Keys mirror `JOB_ICONS` / `JOB_ACCENT_PRESETS` in @crewline/shared, which
 * the API validates against; keep the two lists in step.
 */

/** Curated icons, keyed by lucide name in kebab-case. */
export const JOB_ICONS: ReadonlyArray<{ key: string; label: string; icon: LucideIcon }> = [
  { key: 'building-2', label: 'Tower', icon: Building2Icon },
  { key: 'building', label: 'Building', icon: BuildingIcon },
  { key: 'construction', label: 'Construction', icon: ConstructionIcon },
  { key: 'hard-hat', label: 'Hard hat', icon: HardHatIcon },
  { key: 'hammer', label: 'Hammer', icon: HammerIcon },
  { key: 'factory', label: 'Factory', icon: FactoryIcon },
  { key: 'warehouse', label: 'Warehouse', icon: WarehouseIcon },
  { key: 'hospital', label: 'Hospital', icon: HospitalIcon },
  { key: 'school', label: 'School', icon: SchoolIcon },
  { key: 'house', label: 'Residential', icon: HouseIcon },
  { key: 'landmark', label: 'Civic', icon: LandmarkIcon },
  { key: 'route', label: 'Road', icon: RouteIcon },
  { key: 'radio-tower', label: 'Tower site', icon: RadioTowerIcon },
  { key: 'ship', label: 'Marine', icon: ShipIcon },
  { key: 'plane', label: 'Airport', icon: PlaneIcon },
  { key: 'train-track', label: 'Rail', icon: TrainTrackIcon },
  { key: 'zap', label: 'Electrical', icon: ZapIcon },
  { key: 'droplets', label: 'Water', icon: DropletsIcon },
  { key: 'trees', label: 'Landscape', icon: TreesIcon },
  { key: 'mountain', label: 'Earthworks', icon: MountainIcon },
  { key: 'anchor', label: 'Port', icon: AnchorIcon },
  { key: 'waves', label: 'Waterfront', icon: WavesIcon },
  { key: 'fence', label: 'Fencing', icon: FenceIcon },
  { key: 'wrench', label: 'Maintenance', icon: WrenchIcon },
  { key: 'truck', label: 'Hauling', icon: TruckIcon },
  { key: 'pickaxe', label: 'Mining', icon: PickaxeIcon },
  { key: 'fuel', label: 'Fuel', icon: FuelIcon },
  { key: 'cable', label: 'Utilities', icon: CableIcon },
  { key: 'store', label: 'Retail', icon: StoreIcon },
  { key: 'tent', label: 'Event', icon: TentIcon },
  { key: 'drill', label: 'Drilling', icon: DrillIcon },
  { key: 'tower-control', label: 'Control tower', icon: TowerControlIcon },
  { key: 'dam', label: 'Dam', icon: DamIcon },
  { key: 'tractor', label: 'Agriculture', icon: TractorIcon },
  { key: 'container', label: 'Logistics', icon: ContainerIcon },
  { key: 'brick-wall', label: 'Masonry', icon: BrickWallIcon },
];

export const JOB_ACCENT_PRESETS: ReadonlyArray<{ hex: string; name: string }> = [
  { hex: '#1D4ED8', name: 'Blue' },
  { hex: '#0F766E', name: 'Teal' },
  { hex: '#B45309', name: 'Amber' },
  { hex: '#B91C1C', name: 'Red' },
  { hex: '#6D28D9', name: 'Violet' },
  { hex: '#BE185D', name: 'Pink' },
  { hex: '#0E7490', name: 'Cyan' },
  { hex: '#4D7C0F', name: 'Green' },
  { hex: '#374151', name: 'Slate' },
  { hex: '#0369A1', name: 'Sky' },
];

export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
export const JOB_COVER_MAX_MB = 5;
export const JOB_COVER_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Anything with a look; the shapes the tenant API returns for jobs and for `/auth/me`'s job list. */
export interface JobLook {
  name: string;
  accentColor?: string | null;
  icon?: string | null;
  coverUrl?: string | null;
}

export function jobIcon(key: string | null | undefined): LucideIcon {
  return JOB_ICONS.find((i) => i.key === key)?.icon ?? HardHatIcon;
}

/** Deterministic fallback colour for jobs without an accent, so every job still gets a stable mark. */
export function jobAccent(job: JobLook): string {
  if (job.accentColor && HEX_COLOR_RE.test(job.accentColor)) return job.accentColor;
  let h = 0;
  for (const ch of job.name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return JOB_ACCENT_PRESETS[h % JOB_ACCENT_PRESETS.length]!.hex;
}

/** Black or white text for a solid accent background (WCAG-ish relative luminance). */
export function onAccent(hex: string): '#ffffff' | '#111111' {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45 ? '#111111' : '#ffffff';
}
